import React, { Fragment } from 'react';

import {
  Field,
  Grid,
  Margin,
  Select,
  SelectItem,
  Spacer,
  Text,
  TextInput,
} from '@zapier/design-system';

import AsyncButton from 'app/developer-v3/components/AsyncButton';
import {
  PUBLISHING_FORM_FIELD_BLANK_VALUES as FIELD_BLANK_VALUES,
  useForm,
} from 'app/developer-v3/hooks';

import Step from './Step';
import { PAID_API_OPTIONS } from './constants';
import { ReadinessValues as Values } from './types';
import { useStepCompletion } from './useStepCompletion';
import { getError, parseSaveErrors } from './utils';

type Props = {
  initialValues: Values;
  isDisabled: boolean;
  onComplete: (isComplete: boolean) => void;
  onSubmit: (valuesNext: Values) => Promise<void>;
};

export default function Readiness({
  initialValues,
  isDisabled,
  onComplete,
  onSubmit,
}: Props) {
  const form = useForm<Values>({
    enableReinitialize: true,
    initialValues,
    onSubmit: async formValues => {
      try {
        await onSubmit(formValues);
      } catch (errors) {
        Object.entries(parseSaveErrors(errors)).forEach(([key, value]) => {
          form.setFieldError(key, value as string);
        });
      }
    },
    validate: (values: Values) => {
      const errors: Partial<Record<keyof Values, string>> = {};
      if (values.hasApiOwnership === FIELD_BLANK_VALUES.hasApiOwnership) {
        errors.hasApiOwnership = 'Required.';
      }
      if (values.hasApiOwnership === false) {
        if (values.apiEndpointsUsed === FIELD_BLANK_VALUES.apiEndpointsUsed) {
          errors.apiEndpointsUsed = 'Required.';
        }
        if (values.ownsAllEndpoints === FIELD_BLANK_VALUES.ownsAllEndpoints) {
          errors.ownsAllEndpoints = 'Required.';
        }
        if (
          values.ownsAllEndpoints === false &&
          values.hasPermissionForEndpoints ===
            FIELD_BLANK_VALUES.hasPermissionForEndpoints
        ) {
          errors.hasPermissionForEndpoints = 'Required.';
        }
      }
      if (
        values.usesProductionEndpoints ===
        FIELD_BLANK_VALUES.usesProductionEndpoints
      ) {
        errors.usesProductionEndpoints = 'Required.';
      }
      if ([FIELD_BLANK_VALUES.paidApi, ''].includes(values.paidApi)) {
        errors.paidApi = 'Required.';
      }
      return errors;
    },
    validateOnMount: true,
  });

  const isTouched = Object.values(form.isTouched).some(value => value);
  const isCompleted = !form.isDirty && form.isValid;
  useStepCompletion(isCompleted, onComplete);

  return (
    <Step
      isComplete={!form.isDirty && form.isValid}
      isError={isTouched && !form.isValid}
      title="Integration readiness & API ownership"
    >
      <Grid columns="1" gap="20px">
        <Select
          errorMessage={getError(
            'hasApiOwnership',
            form.isTouched,
            form.errors
          )}
          isDisabled={isDisabled}
          isRequired={true}
          label="Are you using any APIs hosted on a domain you do not own?"
          helpText={`Select "Yes" if an API endpoint is not clearly owned and operated by your company—for example, if the base URL does not include your company's domain name.`}
          isInvalid={
            form.isTouched.hasApiOwnership && !!form.errors.hasApiOwnership
          }
          onBlur={() => form.setFieldTouched('hasApiOwnership', true)}
          onChange={key =>
            form.setFieldValue('hasApiOwnership', key === 'true')
          }
          selectedKey={
            form.values.hasApiOwnership === true
              ? 'true'
              : form.values.hasApiOwnership === false
              ? 'false'
              : null
          }
        >
          {/* Yes means using APIs on domain they don't own, so hasApiOwnership=false */}
          <SelectItem key="false" value="false">
            Yes
          </SelectItem>
          <SelectItem key="true" value="true">
            No
          </SelectItem>
        </Select>
        {form.values.hasApiOwnership === false && (
          <Margin margin="0px 0px 0px 20px">
            <Grid columns="1" gap="20px">
              <Field
                error={getError(
                  'apiEndpointsUsed',
                  form.isTouched,
                  form.errors
                )}
                isDisabled={isDisabled}
                isRequired={true}
                label="Which API endpoints are you using?"
                renderHelpText={() => (
                  <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
                    Type the base API endpoint(s) you're using, e.g.
                    api.yourcompany.com
                  </Text>
                )}
                renderInput={inputProps => (
                  <TextInput
                    {...inputProps}
                    aria-label="Which API endpoints are you using?"
                    name="apiEndpointsUsed"
                    onBlur={form.onBlur}
                    onChange={form.onChange}
                    value={form.values.apiEndpointsUsed}
                  />
                )}
              />
              <Select
                errorMessage={getError(
                  'ownsAllEndpoints',
                  form.isTouched,
                  form.errors
                )}
                isDisabled={isDisabled}
                isRequired={true}
                label="Do you own and operate all of these endpoints?"
                isInvalid={
                  form.isTouched.ownsAllEndpoints &&
                  !!form.errors.ownsAllEndpoints
                }
                onBlur={() => form.setFieldTouched('ownsAllEndpoints', true)}
                onChange={key =>
                  form.setFieldValue('ownsAllEndpoints', key === 'true')
                }
                selectedKey={
                  form.values.ownsAllEndpoints === true
                    ? 'true'
                    : form.values.ownsAllEndpoints === false
                    ? 'false'
                    : null
                }
              >
                <SelectItem key="true" value="true">
                  Yes
                </SelectItem>
                <SelectItem key="false" value="false">
                  No
                </SelectItem>
              </Select>
              {form.values.ownsAllEndpoints === false && (
                <Margin margin="0px 0px 0px 20px">
                  <Field
                    error={getError(
                      'hasPermissionForEndpoints',
                      form.isTouched,
                      form.errors
                    )}
                    isDisabled={isDisabled}
                    isRequired={true}
                    label="Confirm that you have permission from the owners of these endpoints to use them."
                    renderHelpText={() => (
                      <Fragment>
                        <Text
                          color={isDisabled ? 'GrayWarm5' : undefined}
                          fontWeight={700}
                          tag="p"
                        >
                          Be sure to state the company that owns the endpoints
                        </Text>
                        <Text
                          color={isDisabled ? 'GrayWarm5' : undefined}
                          tag="p"
                        >
                          , and that you have permission from that company to
                          use these endpoints in your Zapier integration. We
                          need written confirmation from you that you have
                          permission.
                        </Text>
                      </Fragment>
                    )}
                    renderInput={inputProps => (
                      <TextInput
                        {...inputProps}
                        aria-label="Confirm that you have permission from the owners of these endpoints to use them."
                        name="hasPermissionForEndpoints"
                        onBlur={form.onBlur}
                        onChange={form.onChange}
                        value={form.values.hasPermissionForEndpoints}
                      />
                    )}
                  />
                </Margin>
              )}
            </Grid>
          </Margin>
        )}
        <Select
          errorMessage={getError(
            'usesProductionEndpoints',
            form.isTouched,
            form.errors
          )}
          isDisabled={isDisabled}
          isRequired={true}
          label="Does the integration use production API endpoints?"
          helpText="We need confirmation that the integration uses production API endpoints. Please double-check all API endpoints used in the integration and ensure they're set to production endpoints."
          isInvalid={
            form.isTouched.usesProductionEndpoints &&
            !!form.errors.usesProductionEndpoints
          }
          onBlur={() => form.setFieldTouched('usesProductionEndpoints', true)}
          onChange={key =>
            form.setFieldValue('usesProductionEndpoints', key === 'true')
          }
          selectedKey={
            form.values.usesProductionEndpoints === true
              ? 'true'
              : form.values.usesProductionEndpoints === false
              ? 'false'
              : null
          }
        >
          <SelectItem key="true" value="true">
            Yes
          </SelectItem>
          <SelectItem key="false" value="false">
            No
          </SelectItem>
        </Select>
        <Select
          errorMessage={getError('paidApi', form.isTouched, form.errors)}
          isDisabled={isDisabled}
          isRequired={true}
          label="Do your users need to pay anything additional in order to use this Zapier/API app?"
          isInvalid={form.isTouched.paidApi && !!form.errors.paidApi}
          onBlur={() => form.setFieldTouched('paidApi', true)}
          onChange={key => form.setFieldValue('paidApi', key as string)}
          selectedKey={form.values.paidApi || null}
        >
          {Object.values(PAID_API_OPTIONS).map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </Select>
      </Grid>
      <Spacer height="20px" />
      <AsyncButton
        aria-label="Save integration readiness"
        variant="secondary"
        isDisabled={
          isDisabled || !form.isDirty || !form.isValid || form.isSubmitting
        }
        isWorking={form.isSubmitting}
        onPress={() => form.onSubmit()}
        type="button"
      >
        Save
      </AsyncButton>
    </Step>
  );
}
